import { Record } from 'immutable';
import ConnectionState from './ConnectionState';
import { IWatchdogConfig } from './Watchdog';
import { IDocumentSpec } from 'app/services/rtc/sync/DocumentSpec';
import * as t from 'io-ts';
import { EntityStringId, createEntityStringId } from 'app/types/entity';
import { FilteredReadonlyArrayCodec } from 'app/utils/ioTs';

// We need to filter codecs returned by the RTCPeerConnection object,
// because some of them, like `red` are not actual codecs but
// higher-level encodings.
//
// Based on RFC 3551 https://tools.ietf.org/html/rfc3551. Excluding
// some obscure prioprietary ones and including H264 which is not
// listed but required on Safari
const audioTypes = ['opus', 'g722', 'isac', 'pcmu', 'pcma'] as const;
const videoTypes = ['h263', 'h264', 'h265', 'vp8', 'vp9'] as const;
const audioTypesSet = new Set(audioTypes);
const videoTypesSet = new Set(videoTypes);

export type AudioType = typeof audioTypes[number];
export type VideoType = typeof videoTypes[number];

/**
 * Template for creating deserializers for codecs which are meant to be
 * normalized to lower-case strings
 */
const Codec = <C extends string>(name: string, set: Set<C>) =>
  new t.Type<C, string>(
    name,
    (input): input is C =>
      typeof input === 'string' && (set as Set<string>).has(input),
    (input, context) =>
      typeof input === 'string' && (set as Set<string>).has(input.toLowerCase())
      ? t.success(input.toLowerCase() as C)
      : t.failure(input, context),
    (input) => input,
  );

export const AudioCodec = Codec<AudioType>('AudioCodec', audioTypesSet);
export const VideoCodec = Codec<VideoType>('VideoCodec', videoTypesSet);

/** @deprecated Use IClientFeautes in new code */
export const RTC_FEATURE_PEER_CONNECTION = 'peerConnection';
/** @deprecated Use IClientFeautes in new code */
export const RTC_FEATURE_AUDIO_CODECS    = 'audioCodecs';
/** @deprecated Use IClientFeautes in new code */
export const RTC_FEATURE_VIDEO_CODECS    = 'videoCodecs';

export interface IClientCodecs {
  readonly [RTC_FEATURE_AUDIO_CODECS]: readonly AudioType[];
  readonly [RTC_FEATURE_VIDEO_CODECS]: readonly VideoType[];
}

export interface IClientFeatures extends IClientCodecs {
  readonly [RTC_FEATURE_PEER_CONNECTION]: boolean;
}

interface IEncodedClientFeatures {
  readonly [RTC_FEATURE_AUDIO_CODECS]: readonly string[];
  readonly [RTC_FEATURE_VIDEO_CODECS]: readonly string[];
  readonly [RTC_FEATURE_PEER_CONNECTION]: boolean;
}

const FilteredAudioCodecsArrayCodec = FilteredReadonlyArrayCodec(
  AudioCodec,
  'FilteredAudioCodecsArrayDecoder',
);

const FilteredVideoCodecsArrayCodec = FilteredReadonlyArrayCodec(
  VideoCodec,
  'FilteredVideoCodecsArrayDecoder',
);

const ClientFeaturesCodec: t.Type<IClientFeatures, IEncodedClientFeatures> = t.type({
  [RTC_FEATURE_PEER_CONNECTION]: t.boolean,
  [RTC_FEATURE_AUDIO_CODECS]: FilteredAudioCodecsArrayCodec,
  [RTC_FEATURE_VIDEO_CODECS]: FilteredVideoCodecsArrayCodec,
});

export interface IClientData {
  features: IClientFeatures;
  upgradedToUser?: boolean;
}

export const ClientDataDecoder: t.Decoder<unknown, IClientData> = t.intersection([
  t.type({
    features: ClientFeaturesCodec,
  }),
  t.partial({
    upgradedToUser: t.boolean,
  }),
]).asDecoder();

export const enum WebRTCFeatures {
  PeerConnection = 'PeerConnection',
  UnifiedPlan = 'UnifiedPlan',
  LegacyStreams = 'LegacyStream',
  UserMedia = 'UserMedia',
  DisplayMedia = 'DisplayMedia',
  AutoGain = 'AutoGain',
}

export const RTC_CONNECTING   = ConnectionState.Connecting;
export const RTC_ON           = ConnectionState.On;
export const RTC_FAILED       = ConnectionState.Failed;
export const RTC_OFF          = ConnectionState.Off;

export const RTC_ROOM_EVENTS = {
  signalingStateChange:      'rtcroom/signalingStateChange',
  signalingError:            'rtcroom/signalingError',
  reconnect:                 'rtcroom/reconnect',
  addPeer:                   'rtcroom/addPeer',
  removePeer:                'rtcroom/removePeer',
  botStateChange:            'rtcroom/botStateChange',
  peerConnectionStateChange: 'rtcroom/peerConnectionStateChange',
  peerConnectionError:       'rtcroom/peerConnectionError',
  peerAudioStreamChange:     'rtcroom/peerAudioStreamChange',
  peerVideoStreamChange:     'rtcroom/peerVideoStreamChange',
  peerScreenStreamChange:    'rtcroom/peerScreenStreamChange',
  peerStreamStatusChange:    'rtcroom/peerStreamStatusChange',
  peerUpgradedToUser:        'rtcroom/peerUpgradedToUser',
};

/**
* The maximum length display name that the realtime server will allow for
* the name sent up as part of a join or nameChange request.
*/
export const PARTICIPANT_NAME_MAX_LENGTH = 40;

export interface IDocumentIndex {
  [channel: string]: IDocumentSpec<any, any, any, any> | undefined;
}

/**
 * Debounce this amount of ms before reacting to track-related events.
 * This is useful because when adding multiple tracks at once, some
 * browsers, like Chrome will emit multiple events. Same applies when
 * observing incoming remote track changes.
 */
export const TRACK_EVENTS_DEBOUNCE_TIME = 100;

/**
 * Minimum time to wait before next renegotiation attempt
 */
export const RENEGOTIATION_MIN_INTERVAL = 2000;

/**
 * Watchdog configuration for connecting to signaling server
 */
export const SERVER_WATCHDOG_CONFIG: IWatchdogConfig = {
  name: 'server',
  warningDelay: 5000,
  baseTimeout:  5000,
  maxTimeout:   120000,
};

/**
 * Watchdog configuration for peer connections
 */
export const PEER_WATCHDOG_CONFIG: IWatchdogConfig = {
  name: 'peer',
  warningDelay: 20000,
  baseTimeout:  10000,
  maxTimeout:   120000,
};

export interface ITimeoutWarning {
  /** Number of milliseconds until automatic reconnect */
  timeToReconnect: number;
  /** Timestamp for when the countdown started */
  triggeredAt: number;
}

export const SIGNALING_PROTOCOL = {
  bot: {
    command:      'botCommand',
    stateUpdated: 'botStateUpdated',
  },
  negotiation: {
    // Sent to offerer from if non-offerer needs to renegotiate
    // connection
    needed:          'negotiation_needed',
    sdpOffer:        'sdp_offer',
    sdpAnswer:       'sdp_answer',
    iceCandidate:    'ice_candidate',
  },
  updates:     {
    mediaStatus:     'media_status',
  },
};

// Although, we use 16:9 aspect ratio to display videos, in order for
// them to work OK in both vertical and horizontal orientations on
// mobile devices, use 4:3 and then crop to 16:9 because 4:3 is closer
// to square.
export const RTC_DISPLAY_ASPECT_RATIO = 16 / 9;

// These constraints should only describe desired properties of
// resulting MediaStreamTracks and shouldn't contain any properties
// that can affect selecting devices. Otherwise it would interfere
// with logic in `DeviceState.requestAccess()`. These are the
// constraints that will always be applied, regardless, users'
// preferences.
export const CONSTANT_USER_MEDIA_CONSTRAINTS = {
  audio: {
    channelCount:     { ideal: 1 },
    noiseSuppression: { ideal: true },
    echoCancellation: { ideal: true },
  },
  video: {
    // Drop aspectRatio constraint for now because Safari will drop
    // the deviceId constraint with it. Most devices will select a 4/3
    // aspect ratio anyway
  },
};

interface ICaller {
  id: EntityStringId;
  secret: string;
}

export class Caller extends Record<ICaller>({ id: createEntityStringId(), secret: '' }) {
  public constructor({ id = createEntityStringId(), secret = '' }: Partial<ICaller> = {}) {
    super({ id, secret });
  }
}
